import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, PencilSimple, ArrowCounterClockwise, EyeSlash } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Md } from '@/components/Md';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { fmtDate } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;
const PROGRAMS = ['Todos', 'Super Barriguinhas', 'Super Mamãs', 'Super Pré Mamãs', 'Super Bebés', 'Geral'];
const STATUS_STYLES = { pendente: 'bg-amber-50 text-amber-700', aguarda_confirmacao: 'bg-blue-50 text-blue-700', aprovada: 'bg-green-50 text-green-700', rejeitada: 'bg-red-50 text-red-500', rejeitada_autora: 'bg-red-50 text-red-400' };
const STATUS_LABELS = { pendente: 'Pendente', aguarda_confirmacao: 'Aguarda confirmação', aprovada: 'Aprovada', rejeitada: 'Rejeitada', rejeitada_autora: 'Recusada pela autora' };

export default function AdminRecipesPage() {
  useRequireAdmin();
  const [tab, setTab] = useState('pendente');
  const [recipes, setRecipes] = useState([]);
  const [showEdit, setShowEdit] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showReject, setShowReject] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetch = useCallback(async () => {
    try {
      const estado = tab === 'pendente' ? 'pendente' : tab === 'aprovada' ? 'aprovada' : '';
      let url = `${API}/api/admin/community-recipes/all`;
      if (tab === 'pendente') url = `${API}/api/admin/community-recipes/pending`;
      else if (tab === 'aprovada') url = `${API}/api/admin/community-recipes/all?estado=aprovada`;
      else if (tab === 'rejeitada') url = `${API}/api/admin/community-recipes/all?estado=rejeitada`;
      const { data } = await axios.get(url);
      setRecipes(data);
    } catch {}
  }, [tab]);
  useEffect(() => { fetch(); }, [fetch]);

  const approve = async (id) => { await axios.put(`${API}/api/admin/community-recipes/${id}/approve`); toast.success('Receita aprovada e publicada'); fetch(); };
  const editApprove = async () => {
    if (!showEdit) return;
    await axios.put(`${API}/api/admin/community-recipes/${showEdit}/edit-approve`, editForm);
    toast.success('Edições guardadas. A aguardar confirmação da autora.');
    setShowEdit(null); fetch();
  };
  const reject = async () => {
    if (!showReject) return;
    await axios.put(`${API}/api/admin/community-recipes/${showReject}/reject`, { reason: rejectReason });
    toast.success('Receita rejeitada'); setShowReject(null); setRejectReason(''); fetch();
  };
  const unpublish = async (id) => { await axios.put(`${API}/api/admin/community-recipes/${id}/unpublish`); toast.success('Receita despublicada'); fetch(); };
  const reactivate = async (id) => { await axios.put(`${API}/api/admin/community-recipes/${id}/reactivate`); toast.success('Receita reactivada'); fetch(); };

  return (
    <div className="fade-in" data-testid="admin-recipes-page">
      <div className="mb-5">
        <h2 className="font-heading font-medium text-xl text-brand-text">Receitas da Comunidade</h2>
        <p className="text-xs text-brand-muted mt-0.5">Gestão de submissões das clientes</p>
      </div>

      <div className="flex gap-2 mb-5">
        {[{k:'pendente',l:'Pendentes'},{k:'aprovada',l:'Aprovadas'},{k:'rejeitada',l:'Rejeitadas'}].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`flex-1 py-2.5 rounded-xl text-sm font-medium ${tab === t.k ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{t.l}</button>
        ))}
      </div>

      {recipes.length === 0 ? (
        <p className="text-center text-brand-muted py-8">Sem receitas {tab === 'pendente' ? 'pendentes' : tab === 'aprovada' ? 'aprovadas' : 'rejeitadas'}.</p>
      ) : (
        <div className="space-y-3">
          {recipes.map(r => (
            <div key={r.id} className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`admin-recipe-${r.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-sm font-medium text-brand-text">{r.titulo}</h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[9px] bg-brand-bg text-brand-muted px-2 py-0.5 rounded-full">{r.programa}</span>
                    <span className={`text-[9px] px-2 py-0.5 rounded-full ${STATUS_STYLES[r.estado] || ''}`}>{STATUS_LABELS[r.estado] || r.estado}</span>
                    <span className="text-[9px] text-brand-muted">por {r.autora_nome}{r.mostrar_nome_autora ? '' : ' (anónima)'}</span>
                  </div>
                </div>
                <span className="text-[9px] text-brand-placeholder">{fmtDate(r.data_submissao)}</span>
              </div>

              <div className="bg-brand-bg/20 rounded-xl p-3 mb-2 text-xs">
                <p className="text-[9px] text-brand-muted uppercase mb-1">Ingredientes</p>
                <Md as="p" className="text-brand-text whitespace-pre-line">{r.ingredientes}</Md>
                {r.modo_preparacao && (<><p className="text-[9px] text-brand-muted uppercase mt-2 mb-1">Preparação</p><Md as="p" className="text-brand-text whitespace-pre-line">{r.modo_preparacao}</Md></>)}
              </div>

              {r.aguarda_confirmacao_autora && (
                <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-xl mb-2 text-xs text-blue-700">
                  Aguarda confirmação da autora
                </div>
              )}
              {r.motivo_rejeicao && (
                <div className="p-2 bg-red-50 border border-red-200 rounded-xl mb-2 text-xs text-red-600">
                  Motivo: {r.motivo_rejeicao}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 flex-wrap">
                {tab === 'pendente' && !r.aguarda_confirmacao_autora && (
                  <>
                    <button onClick={() => approve(r.id)} className="flex items-center gap-1 px-3 py-1.5 bg-brand-green text-white rounded-full text-xs font-medium"><Check size={12} /> Aprovar</button>
                    <button onClick={() => { setShowEdit(r.id); setEditForm({ titulo: r.titulo, ingredientes: r.ingredientes, modo_preparacao: r.modo_preparacao || '', programa: r.programa }); }} className="flex items-center gap-1 px-3 py-1.5 border border-brand-border text-brand-green rounded-full text-xs font-medium"><PencilSimple size={12} /> Editar e aprovar</button>
                    <button onClick={() => { setShowReject(r.id); setRejectReason(''); }} className="flex items-center gap-1 px-3 py-1.5 border border-red-200 text-red-500 rounded-full text-xs font-medium"><X size={12} /> Rejeitar</button>
                  </>
                )}
                {tab === 'aprovada' && (
                  <button onClick={() => unpublish(r.id)} className="flex items-center gap-1 px-3 py-1.5 border border-brand-border text-brand-muted rounded-full text-xs font-medium"><EyeSlash size={12} /> Despublicar</button>
                )}
                {tab === 'rejeitada' && (
                  <button onClick={() => reactivate(r.id)} className="flex items-center gap-1 px-3 py-1.5 border border-brand-green/30 text-brand-green rounded-full text-xs font-medium"><ArrowCounterClockwise size={12} /> Reativar</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit modal */}
      <Dialog open={!!showEdit} onOpenChange={() => setShowEdit(null)}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Editar e aprovar</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Título</Label><Input value={editForm.titulo || ''} onChange={e => setEditForm({ ...editForm, titulo: e.target.value })} className="rounded-xl border-brand-border" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Ingredientes</Label><Textarea value={editForm.ingredientes || ''} onChange={e => setEditForm({ ...editForm, ingredientes: e.target.value })} className="rounded-xl border-brand-border" rows={4} /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Modo de preparação</Label><Textarea value={editForm.modo_preparacao || ''} onChange={e => setEditForm({ ...editForm, modo_preparacao: e.target.value })} className="rounded-xl border-brand-border" rows={4} /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Programa</Label>
              <div className="flex gap-1.5 flex-wrap">{PROGRAMS.map(p => (<button key={p} type="button" onClick={() => setEditForm({ ...editForm, programa: p })} className={`px-3 py-1.5 rounded-full text-[11px] font-medium ${editForm.programa === p ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{p}</button>))}</div>
            </div>
            <p className="text-[10px] text-brand-muted">A autora será notificada para confirmar as alterações antes da publicação.</p>
            <button onClick={editApprove} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">Guardar e enviar para confirmação</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject modal */}
      <Dialog open={!!showReject} onOpenChange={() => setShowReject(null)}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Rejeitar receita</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Motivo (opcional)</Label><Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Explica à autora porque a receita não foi aprovada..." className="rounded-xl border-brand-border" rows={3} /></div>
            <button onClick={reject} className="w-full bg-red-500 text-white rounded-full py-3 font-medium">Confirmar rejeição</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
